import { useParams } from 'react-router-dom';
import { SyntheticEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
//
import {
  Box,
  Card,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
} from '@mui/material';
import { Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import ClientBookingsTableRow from './ClientBookingsTableRow';
// api
import { getUserBookings } from '@/features/users/shared/api';
import { getFilteredBookingSession } from '@/features/bookings-management/api';
// hookes + utils
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useTabs from '@/hooks/useTabs';
import {
  CLIENT_BOOKING_SESSION_FILTER_TABS,
  CLIENT_BOOKINGS_TABLE_HEAD,
  applySortFilterClientBookings,
} from '@/features/users/clients/utils';
import type {
  Booking,
  BookingSessionCount,
} from '@/features/bookings-management/types';

const SESSION_COUNT_FILTERS = [
  { filter: 'previous', countKey: 'completed' },
  { filter: 'today', countKey: 'today' },
  { filter: 'upcoming', countKey: 'upcoming' },
] as const;

type ClientBookingSessionCountMap = Record<number, BookingSessionCount>;

const getReconciledBookingSessionCounts = async (
  bookings: Booking[]
): Promise<ClientBookingSessionCountMap> => {
  const countEntries = await Promise.all(
    bookings.map(async (booking) => {
      const fallbackCounts = booking.booking_sessions_count ?? {
        completed: 0,
        today: 0,
        upcoming: 0,
      };
      const results = await Promise.all(
        SESSION_COUNT_FILTERS.map(async ({ filter }) => {
          try {
            return await getFilteredBookingSession(booking.id, filter);
          } catch {
            return null;
          }
        })
      );
      const counts = { ...fallbackCounts };

      results.forEach((sessions, index) => {
        if (!sessions) return;

        const countKey = SESSION_COUNT_FILTERS[index].countKey;
        counts[countKey] = sessions.length;
      });

      return [booking.id, counts] as const;
    })
  );

  return Object.fromEntries(countEntries);
};

export default function ClientBookings() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const allow = useGetAccess('bookings');
  const { userId } = useParams();
  const { errorMsg } = useSnackbarMsg();

  const [searchFieldValue, setSearchFieldValue] = useState('');
  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } =
    useTabs('active');
  // =================
  //      QUERIES
  // =================
  const { data: clientBookings, isLoading: isClientDataLoading } = useQuery({
    queryKey: ['clientBookings', userId],
    queryFn: () => getUserBookings(Number(userId)),
    enabled: !!userId,
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching clients`);
    },
  });
  const clientBookingIds = useMemo(
    () => (clientBookings ?? []).map((booking) => booking.id),
    [clientBookings]
  );
  const { data: reconciledSessionCounts, isLoading: isSessionCountsLoading } =
    useQuery({
      queryKey: ['clientBookingSessionCounts', userId, clientBookingIds],
      queryFn: () => getReconciledBookingSessionCounts(clientBookings ?? []),
      enabled: !!clientBookings?.length,
      refetchOnWindowFocus: false,
    });
  const clientBookingsWithSessionCounts = useMemo(
    () =>
      (clientBookings ?? []).map((booking) => ({
        ...booking,
        booking_sessions_count:
          reconciledSessionCounts?.[booking.id] ??
          booking.booking_sessions_count,
      })),
    [clientBookings, reconciledSessionCounts]
  );

  const dataFiltered = applySortFilterClientBookings({
    tableData: clientBookingsWithSessionCounts,
    comparator: getComparator(order, orderBy),
    searchFieldValue,
    filterStatus,
  });

  const denseHeight = dense ? 52 : 72;
  const isLoadingSessionAwareBookings =
    isClientDataLoading || (!!clientBookings?.length && isSessionCountsLoading);

  const isNotFound =
    (!dataFiltered.length && !!searchFieldValue) || !dataFiltered.length;

  const handleFilterSearchValue = (value: string) => {
    setSearchFieldValue(value);
    setPage(0);
  };

  const handleFilterStatus = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    onChangeFilterStatus(event, newValue);
    setPage(0);
  };

  return (
    <Card>
      {isLoadingSessionAwareBookings ? (
        <SkeletonLoadingTable />
      ) : (
        <>
          <Tabs
            allowScrollButtonsMobile
            variant="scrollable"
            scrollButtons="auto"
            value={filterStatus}
            onChange={handleFilterStatus}
            sx={{ px: 2, bgcolor: 'background.neutral' }}
          >
            {CLIENT_BOOKING_SESSION_FILTER_TABS.map((tab) => (
              <Tab
                disableRipple
                key={tab.value}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>

          <Divider />
          <ToolbarWithSearchAndStatus
            filterText={searchFieldValue}
            onFilterText={handleFilterSearchValue}
            labels={{ search: `Search in client's bookings...` }}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1100, position: 'relative' }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={CLIENT_BOOKINGS_TABLE_HEAD}
                  rowCount={clientBookingsWithSessionCounts.length}
                  numSelected={selected.length}
                  onSort={onSort}
                />
                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((booking) => (
                      <ClientBookingsTableRow
                        key={booking.id}
                        row={booking}
                        canDelete={allow.canDelete}
                        canUpdate={allow.canUpdate}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      dataFiltered.length
                    )}
                  />

                  <TableNoData
                    isNotFound={isNotFound}
                    title="No bookings found"
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </>
      )}
    </Card>
  );
}
